import { oc } from '@orpc/contract';
import * as z from 'zod';
import { configRevisionSchema } from '../domain/config.js';
import { deviceActivitySchema } from '../domain/device-activity.js';
import { API_VERSION } from './version.js';
const base = `/${API_VERSION}/device`;
/**
 * The device's own surface. Authenticated with the scoped device token only —
 * a parent access token must never reach a tablet (SPEC.md §19, §38).
 */
export const deviceConfigContract = {
    /**
     * Fetches the current snapshot, or confirms the held one is current.
     *
     * The device sends the revision it already has. `unchanged` means keep what
     * you have, which keeps the common case cheap on a tablet's connection.
     */
    getConfig: oc
        .route({ method: 'GET', path: `${base}/config` })
        .input(z.object({ knownRevision: z.coerce.number().int().nonnegative().optional() }))
        .output(z.discriminatedUnion('status', [
        z.object({ status: z.literal('unchanged') }),
        z.object({ status: z.literal('updated'), config: configRevisionSchema }),
    ]))
        .errors({ DEVICE_UNPAIRED: { status: 403, message: 'This device is no longer paired' } }),
    /**
     * Liveness, and the server's own time.
     *
     * The device compares that time against its own to notice a clock that has
     * drifted or been changed, which is what stops a wrong clock silently
     * resetting a screen-time budget (SPEC.md §19.2).
     */
    heartbeat: oc
        .route({ method: 'POST', path: `${base}/heartbeat` })
        .input(z.object({
        currentRevision: z.int().nonnegative(),
        /**
         * What the tablet is doing right now. Optional only so a tablet still on
         * an older build keeps beating; every current build sends it.
         */
        activity: deviceActivitySchema.optional(),
    }))
        .output(z.object({
        serverTime: z.iso.datetime(),
        configRevision: z.int().nonnegative(),
        /**
         * Whether a parent has paused this tablet's child, as the server holds it.
         *
         * The tablet learns of a pause or resume from a command, and a command
         * can be missed: asleep, offline, mid-reconnect. With only the command,
         * a missed RESUME left every video paused until the app was killed.
         * Repeated on every beat, the tablet's own idea of "paused" can never
         * drift from the server's for longer than one beat.
         */
        isParentPaused: z.boolean(),
    })),
};
//# sourceMappingURL=device-config.js.map